import { useState, useEffect, useRef, useCallback } from "react";
import { trpc } from "@/hooks/trpc";
import { formatTime } from "@/lib/utils";

const AUTO_CLOSE_MS = 30 * 1000; // 30 seconds

interface CompactBookingFlowProps {
  roomId: string;
  slotStart: Date;
  slotEnd: Date;
  onClose: () => void;
}

const DURATIONS = [
  { label: "15m", value: 15 },
  { label: "30m", value: 30 },
  { label: "45m", value: 45 },
  { label: "1h", value: 60 },
  { label: "1.5h", value: 90 },
  { label: "2h", value: 120 },
];

export function CompactBookingFlow({
  roomId,
  slotStart,
  slotEnd,
  onClose,
}: CompactBookingFlowProps) {
  const [duration, setDuration] = useState<number>(30);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onClose(), AUTO_CLOSE_MS);
  }, [onClose]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resetTimer]);

  const handleInteraction = () => resetTimer();

  const utils = trpc.useUtils();

  const createBooking = trpc.bookings.create.useMutation({
    onSuccess: () => {
      utils.bookings.listByRoom.invalidate({ room_id: roomId });
      onClose();
    },
  });

  // "Now" is the actual current time (seconds zeroed)
  const now = new Date();
  now.setSeconds(0, 0);

  const maxDuration = Math.floor(
    (slotEnd.getTime() - now.getTime()) / 60000,
  );
  const availableDurations = DURATIONS.filter((d) => d.value <= maxDuration);

  const effectiveDuration = availableDurations.some((d) => d.value === duration)
    ? duration
    : availableDurations[0]?.value ?? 15;

  const endTime = new Date(now.getTime() + effectiveDuration * 60000);

  const handleSubmit = () => {
    // Grab fresh "now" at submit time
    const start = new Date();
    start.setSeconds(0, 0);
    const end = new Date(start.getTime() + effectiveDuration * 60000);

    createBooking.mutate({
      room_id: roomId,
      start_time: start.toISOString(),
      end_time: end.toISOString(),
    });
  };

  const noTime = availableDurations.length === 0;
  const minutesUntilNext = Math.max(
    0,
    Math.ceil((slotEnd.getTime() - now.getTime()) / 60000),
  );
  const nextMeetingStr = formatTime(slotEnd.toISOString());

  const nowStr = formatTime(now.toISOString());

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className="relative z-10 w-[360px] rounded-2xl p-5"
        style={{
          background: "rgba(30, 30, 30, 0.95)",
          border: "1px solid rgba(255,255,255,0.1)",
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleInteraction();
        }}
        onTouchStart={handleInteraction}
      >
        {noTime ? (
          <>
            <p className="text-base font-semibold text-white/90">
              No immediate time available
            </p>
            <div
              className="mt-3 flex items-center gap-2.5 rounded-lg px-3 py-2.5"
              style={{ background: "rgba(255,255,255,0.06)" }}
            >
              <div className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-white/80">
                  Next meeting starts in {minutesUntilNext} minute
                  {minutesUntilNext === 1 ? "" : "s"}
                </p>
                <p className="text-xs text-white/35 tabular-nums">
                  at {nextMeetingStr}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="mt-5 w-full rounded-lg py-3 text-sm font-medium text-white/70 bg-white/10 hover:bg-white/20 transition-colors"
            >
              Close
            </button>
          </>
        ) : (
          <>
        <p className="text-base font-semibold text-white/90">Book Room</p>

        {/* "Starting now" — prominent */}
        <div
          className="mt-3 flex items-center gap-2.5 rounded-lg px-3 py-2.5"
          style={{ background: "rgba(255,255,255,0.06)" }}
        >
          <div className="h-2 w-2 rounded-full bg-farmless-green-7 animate-pulse-dot shrink-0" />
          <div>
            <p className="text-sm font-semibold text-white/80">Starting now</p>
            <p className="text-xs text-white/35 tabular-nums">{nowStr}</p>
          </div>
        </div>

        {/* Duration chips */}
        <p className="text-[10px] font-medium text-white/35 uppercase tracking-widest mt-4 mb-2">
          How long?
        </p>
        <div className="flex flex-wrap gap-2">
          {availableDurations.map((d) => {
            const isSelected = effectiveDuration === d.value;
            const btnEnd = new Date(now.getTime() + d.value * 60000);
            return (
              <button
                key={d.value}
                onClick={() => setDuration(d.value)}
                className={`rounded-lg px-4 py-2.5 text-sm transition-colors ${
                  isSelected
                    ? "bg-white text-black"
                    : "bg-white/10 text-white/70 hover:bg-white/20"
                }`}
              >
                <span className="font-semibold">{d.label}</span>
                <span
                  className={`ml-1.5 text-xs ${isSelected ? "text-black/40" : "text-white/30"}`}
                >
                  ends {formatTime(btnEnd.toISOString())}
                </span>
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg py-3 text-sm font-medium text-white/50 bg-white/5 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createBooking.isPending}
            className="flex-1 rounded-lg py-3 text-sm font-semibold text-black bg-white hover:bg-white/90 transition-colors disabled:opacity-40"
          >
            {createBooking.isPending ? "Booking..." : "Confirm"}
          </button>
        </div>

        {createBooking.isError && (
          <p className="text-xs text-red-400 mt-2">
            {createBooking.error.message}
          </p>
        )}
          </>
        )}
      </div>
    </div>
  );
}
